const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function bindWithdrawalTriggers () {
  document.querySelectorAll('[data-tb-eu-withdrawal-trigger]').forEach((trigger) => {
    if (trigger.dataset.euWithdrawalBound === 'true') return
    trigger.dataset.euWithdrawalBound = 'true'
    trigger.addEventListener('click', handleTriggerOpen)
  })
}

function handleTriggerOpen () {
  const template = document.getElementById('tb-eu-withdrawal-form-template')
  const modal = document.querySelector('reusable-modal')
  if (!template || !modal) return

  const modalTitle = modal.querySelector('.reusable-modal__title')
  const modalBody = modal.querySelector('.reusable-modal__body')

  if (modalTitle) {
    modalTitle.textContent = ''
  }

  if (modalBody) {
    modalBody.innerHTML = ''
    modalBody.appendChild(template.content.cloneNode(true))
  }

  window.dispatchEvent(new CustomEvent('open:modal', {
    detail: {
      width: 'min(560px, 100svw)',
      padding: 'none',
      modalClassAdd: ['w-full', 'md:min-w-[400px]'],
      headerClassAdd: ['absolute', 'h-[60px]', 'top-0', 'left-0', 'right-0', 'z-20', 'block'],
      headerClassRemove: ['border-b', 'grid', 'mb-4', 'pb-2', 'border-clay'],
      bodyClassAdd: ['relative', 'z-10', 'p-4', 'md:p-6'],
      closeClassAdd: ['absolute', 'top-4', 'right-4', 'z-20'],
    },
  }))
}

class EuWithdrawal extends HTMLElement {
  constructor () {
    super()
    this.formData = {}
    this.isTransitioning = false
    this.isSubmitting = false
  }

  connectedCallback () {
    this.endpointUrl = this.dataset.endpointUrl
    this.store = this.dataset.store
    this.proceedButtonText = this.dataset.proceedButtonText || 'Proceed with withdrawal'
    this.editButtonText = this.dataset.editButtonText || 'Edit details'
    this.confirmButtonText = this.dataset.confirmButtonText || 'Confirm withdrawal'
    this.successHeading = this.dataset.successHeading || 'Withdrawal request received'
    this.successBody = this.dataset.successBody || 'We have received your withdrawal request.'
    this.acknowledgementMessage = this.dataset.acknowledgementMessage || 'A confirmation has been sent to {email}.'
    this.errorMessage = this.dataset.errorMessage || 'Something went wrong. Please try again.'
    this.loadingMessage = this.dataset.loadingMessage || 'Submitting...'
    this.validationName = this.dataset.validationName || 'Please enter your name'
    this.validationOrder = this.dataset.validationOrder || 'Please enter your order reference'
    this.validationEmail = this.dataset.validationEmail || 'Please enter a valid email address'
    this.orderPrefix = (this.dataset.orderPrefix || '').trim()

    this.formView = this.querySelector('[data-view-type="form"]')
    this.reviewView = this.querySelector('[data-view-type="review"]')
    this.resultView = this.querySelector('[data-view-type="result"]')

    this.setupEventListeners()
  }

  setupEventListeners () {
    const proceedButton = this.querySelector('[data-action="proceed"]')
    const editButton = this.querySelector('[data-action="edit"]')
    const confirmButton = this.querySelector('[data-action="confirm"]')

    if (proceedButton) {
      proceedButton.addEventListener('click', () => this.handleProceed())
    }

    if (editButton) {
      editButton.addEventListener('click', () => this.handleEdit())
    }

    if (confirmButton) {
      confirmButton.addEventListener('click', () => this.handleConfirm())
    }

    this.querySelectorAll('.eu-withdrawal__input').forEach((input) => {
      input.addEventListener('input', () => this.hideError(input))
    })
  }

  getInput (name) {
    return this.querySelector(`[name="${name}"]`)
  }

  hideError (input) {
    const field = input.closest('.eu-withdrawal__field')
    if (!field) return
    const error = field.querySelector('.eu-withdrawal__error')
    if (error) error.classList.add('hidden')
    input.removeAttribute('aria-invalid')
  }

  showError (input, message) {
    const field = input.closest('.eu-withdrawal__field')
    if (!field) return
    const error = field.querySelector('.eu-withdrawal__error')
    if (error) {
      const span = error.querySelector('span')
      if (span && message) span.textContent = message
      error.classList.remove('hidden')
    }
    input.setAttribute('aria-invalid', 'true')
    input.focus({ preventScroll: true })
  }

  normalizeOrderReference (value) {
    let reference = (value || '').trim().replace(/^#+/, '')

    if (this.orderPrefix && reference.toUpperCase().startsWith(this.orderPrefix.toUpperCase())) {
      reference = reference.slice(this.orderPrefix.length)
    }

    if (this.orderPrefix) {
      return this.orderPrefix + reference
    }

    return reference
  }

  getOrderSuffix (value) {
    let suffix = (value || '').trim().replace(/^#+/, '')

    if (this.orderPrefix && suffix.toUpperCase().startsWith(this.orderPrefix.toUpperCase())) {
      suffix = suffix.slice(this.orderPrefix.length)
    }

    return suffix
  }

  validateForm () {
    const nameInput = this.getInput('name')
    const orderInput = this.getInput('order_reference')
    const emailInput = this.getInput('email')

    const name = (nameInput?.value || '').trim()
    const orderSuffix = this.getOrderSuffix(orderInput?.value)
    const orderReference = this.normalizeOrderReference(orderInput?.value)
    const email = (emailInput?.value || '').trim()

    if (!name) {
      this.showError(nameInput, this.validationName)
      return null
    }

    if (!orderSuffix) {
      this.showError(orderInput, this.validationOrder)
      return null
    }

    if (!email || !EMAIL_PATTERN.test(email)) {
      this.showError(emailInput, this.validationEmail)
      return null
    }

    if (orderInput && this.orderPrefix) {
      orderInput.value = orderSuffix
    }

    return { name, orderReference, email }
  }

  populateReview () {
    const nameEl = this.querySelector('.eu-withdrawal__review-name')
    const orderEl = this.querySelector('.eu-withdrawal__review-order')
    const emailEl = this.querySelector('.eu-withdrawal__review-email')

    if (nameEl) nameEl.textContent = this.formData.name
    if (orderEl) orderEl.textContent = this.formData.orderReference
    if (emailEl) emailEl.textContent = this.formData.email
  }

  handleProceed () {
    if (this.isTransitioning) return

    const validated = this.validateForm()
    if (!validated) return

    this.formData = validated
    this.populateReview()
    this.transitionToView(this.reviewView)
  }

  handleEdit () {
    if (this.isTransitioning) return
    this.transitionToView(this.formView)
  }

  async handleConfirm () {
    if (this.isTransitioning || this.isSubmitting) return

    const confirmButton = this.querySelector('[data-action="confirm"]')
    this.isSubmitting = true
    if (confirmButton) confirmButton.classList.add('eu-withdrawal__button--loading')

    try {
      const response = await fetch(this.endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          name: this.formData.name,
          orderReference: this.formData.orderReference,
          email: this.formData.email,
          shop: this.store,
          submittedAt: new Date().toISOString(),
        }),
      })

      let payload = {}
      try {
        payload = await response.json()
      } catch {
        payload = {}
      }

      if (!response.ok || !payload.ok) {
        throw new Error(payload.message || this.errorMessage)
      }

      const receivedAt = payload.received_at || payload.receivedAt || new Date().toISOString()
      this.renderResult(true, receivedAt)
      await this.transitionToView(this.resultView)
      this.shrinkModalForResult()
    } catch {
      this.renderResult(false)
      await this.transitionToView(this.resultView)
      this.shrinkModalForResult()
    } finally {
      this.isSubmitting = false
      if (confirmButton) confirmButton.classList.remove('eu-withdrawal__button--loading')
    }
  }

  formatTimestamp (isoString) {
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'long',
        timeStyle: 'short',
      }).format(new Date(isoString))
    } catch {
      return isoString
    }
  }

  shrinkModalForResult () {
    const modal = document.querySelector('reusable-modal')
    const modalEl = modal?.querySelector('.reusable-modal')
    const bodyWrapper = modal?.querySelector('.reusable-modal__body-wrapper')

    if (modalEl) {
      modalEl.classList.remove('min-h-[200px]')
      modalEl.style.minHeight = 'auto'
    }

    if (bodyWrapper) {
      bodyWrapper.classList.remove('flex-grow')
    }
  }

  renderResult (success, receivedAt) {
    const container = this.querySelector('.eu-withdrawal__result')
    if (!container) return

    if (success) {
      const formatted = this.formatTimestamp(receivedAt)
      const acknowledgement = this.acknowledgementMessage
        .replace(/\{email\}/g, this.formData.email)
        .replace(/\{timestamp\}/g, formatted)

      container.innerHTML = `
        <div class="eu-withdrawal__result--success">
          <div class="eu-withdrawal__heading heading-base font-medium mb-5">${this.escapeHtml(this.successHeading)}</div>
          <p class="text-base text-iron mb-2">${this.escapeHtml(this.successBody)}</p>
          <p class="text-base text-iron">${this.escapeHtml(acknowledgement)}</p>
          <p class="text-sm text-iron mt-3">${this.escapeHtml(formatted)}</p>
        </div>
      `
    } else {
      container.innerHTML = `
        <div class="eu-withdrawal__result--error">
          <p class="text-base text-danger mb-4">${this.escapeHtml(this.errorMessage)}</p>
          <button type="button" class="button button-outline h-[46px] w-full" data-action="retry">
            <span>${this.escapeHtml(this.editButtonText)}</span>
          </button>
        </div>
      `

      const retryButton = container.querySelector('[data-action="retry"]')
      if (retryButton) {
        retryButton.addEventListener('click', () => {
          this.transitionToView(this.reviewView)
        })
      }
    }
  }

  escapeHtml (value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  transitionToView (nextViewElement) {
    return new Promise((resolve) => {
      if (!nextViewElement) {
        resolve()
        return
      }

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      const currentView = this.querySelector('.eu-withdrawal__view--active')
      this.isTransitioning = true

      if (prefersReducedMotion || !currentView) {
        if (currentView) currentView.classList.remove('eu-withdrawal__view--active')
        nextViewElement.classList.add('eu-withdrawal__view--active')
        this.isTransitioning = false
        resolve()
        return
      }

      if (currentView) {
        currentView.classList.add('eu-withdrawal__view--leaving')
      }

      setTimeout(() => {
        if (currentView) {
          currentView.classList.remove('eu-withdrawal__view--active', 'eu-withdrawal__view--leaving')
        }

        nextViewElement.classList.add('eu-withdrawal__view--active', 'eu-withdrawal__view--entering')

        requestAnimationFrame(() => {
          nextViewElement.classList.add('eu-withdrawal__view--entering-active')
        })

        setTimeout(() => {
          nextViewElement.classList.remove('eu-withdrawal__view--entering', 'eu-withdrawal__view--entering-active')
          this.isTransitioning = false
          resolve()
        }, 300)
      }, 200)
    })
  }
}

if (!customElements.get('eu-withdrawal')) {
  customElements.define('eu-withdrawal', EuWithdrawal)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindWithdrawalTriggers)
} else {
  bindWithdrawalTriggers()
}
